import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { personalInfo } from '../../data/mockData';
import { Twitter, Linkedin, Instagram, Sparkles } from 'lucide-react';
import { EditableText, EditableImage } from '../admin/InlineEditor';
import { FloatingShape } from './FloatingShape';

const HeroSection = () => {
  const sceneRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const mouseX = useSpring(mx, { stiffness: 55, damping: 18 });
  const mouseY = useSpring(my, { stiffness: 55, damping: 18 });

  const handleMouseMove = (e) => {
    const rect = sceneRef.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const resetMouse = () => {
    mx.set(0);
    my.set(0);
  };

  // Ambient background orbs — depth layer 1 (subtle parallax)
  const orb1 = {
    x: useTransform(mouseX, [-0.5, 0.5], [-45, 45]),
    y: useTransform(mouseY, [-0.5, 0.5], [-30, 30]),
  };
  const orb2 = {
    x: useTransform(mouseX, [-0.5, 0.5], [55, -55]),
    y: useTransform(mouseY, [-0.5, 0.5], [40, -40]),
  };
  const orb3 = {
    x: useTransform(mouseX, [-0.5, 0.5], [-25, 25]),
    y: useTransform(mouseY, [-0.5, 0.5], [25, -25]),
  };

  // Floating 3D geometric shapes — depth layer 2 (deep parallax + entrance + idle motion)
  const s1 = { x: useTransform(mouseX, [-0.5, 0.5], [-90, 90]), y: useTransform(mouseY, [-0.5, 0.5], [-60, 60]) };
  const s2 = { x: useTransform(mouseX, [-0.5, 0.5], [85, -85]), y: useTransform(mouseY, [-0.5, 0.5], [70, -70]) };
  const s3 = { x: useTransform(mouseX, [-0.5, 0.5], [-70, 70]), y: useTransform(mouseY, [-0.5, 0.5], [55, -55]) };
  const s4 = { x: useTransform(mouseX, [-0.5, 0.5], [65, -65]), y: useTransform(mouseY, [-0.5, 0.5], [-50, 50]) };
  const s5 = { x: useTransform(mouseX, [-0.5, 0.5], [-50, 50]), y: useTransform(mouseY, [-0.5, 0.5], [-75, 75]) };
  const s6 = { x: useTransform(mouseX, [-0.5, 0.5], [45, -45]), y: useTransform(mouseY, [-0.5, 0.5], [60, -60]) };

  const cardTiltX = useTransform(mouseY, [-0.5, 0.5], [8, -8]);
  const cardTiltY = useTransform(mouseX, [-0.5, 0.5], [-8, 8]);

  return (
    <section
      ref={sceneRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={resetMouse}
      data-testid="hero-3d-scene"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#09090b]"
      style={{ perspective: 1500 }}
    >
      {/* Charcoal base + fine grain pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.04%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />

      {/* Ambient Orbs — monochrome, entrance scale-in */}
      <motion.div style={orb1} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4 }} className="absolute top-20 left-10 w-72 h-72 bg-white/[0.06] rounded-full blur-3xl animate-pulse" />
      <motion.div style={orb2} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.1 }} className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl animate-pulse" />
      <motion.div style={orb3} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.2 }} className="absolute top-1/2 left-1/2 w-64 h-64 bg-white/[0.04] rounded-full blur-3xl animate-pulse" />

      {/* Floating 3D shapes — interactive scene, load with entrance + continuous idle motion */}
      <FloatingShape parallaxX={s1.x} parallaxY={s1.y} delay={0.1} floatDuration={5} spinDuration={22} className="top-28 right-[12%] w-16 h-16" testId="hero-3d-shape-cube">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 border border-white/20 shadow-xl shadow-black/40 backdrop-blur-sm" />
      </FloatingShape>
      <FloatingShape parallaxX={s2.x} parallaxY={s2.y} delay={0.2} floatDuration={6} spinDuration={28} spinReverse className="bottom-32 left-[10%] w-20 h-20" testId="hero-3d-shape-ring">
        <div className="w-20 h-20 rounded-full border-4 border-white/25 shadow-xl shadow-black/30" />
      </FloatingShape>
      <FloatingShape parallaxX={s3.x} parallaxY={s3.y} delay={0.3} floatDuration={4.5} spinDuration={18} className="top-[38%] left-[6%] w-10 h-10" testId="hero-3d-shape-diamond">
        <div className="w-10 h-10 rotate-45 rounded-md bg-gradient-to-br from-white/25 to-white/5 border border-white/25" />
      </FloatingShape>
      <FloatingShape parallaxX={s4.x} parallaxY={s4.y} delay={0.4} floatDuration={5.5} spinDuration={24} spinReverse className="top-[65%] right-[8%] w-12 h-12" testId="hero-3d-shape-triangle">
        <div
          className="w-12 h-12 bg-gradient-to-br from-white/20 to-white/5 border border-white/20"
          style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
        />
      </FloatingShape>
      <FloatingShape parallaxX={s5.x} parallaxY={s5.y} delay={0.5} floatDuration={4} spinDuration={16} className="top-[14%] left-[45%] w-6 h-6" testId="hero-3d-shape-dot">
        <div className="w-6 h-6 rounded-full bg-white/30 shadow-lg shadow-white/10" />
      </FloatingShape>
      <FloatingShape parallaxX={s6.x} parallaxY={s6.y} delay={0.6} floatDuration={6.5} spinDuration={20} spinReverse className="bottom-[16%] right-[32%] w-14 h-14" testId="hero-3d-shape-hex">
        <div className="w-14 h-14 border-2 border-white/20 rounded-full" style={{ clipPath: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)' }} />
      </FloatingShape>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Profile Card */}
          <div className="flex justify-center lg:justify-end order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotateY: -25, y: 40 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{ rotateX: cardTiltX, rotateY: cardTiltY, transformPerspective: 1200, transformStyle: 'preserve-3d' }}
              className="relative group"
            >
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-lg opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

              <div className="relative bg-white/[0.04] backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-white/10 shadow-2xl shadow-black/50">
                {/* Profile Image */}
                <div className="flex justify-center mb-6">
                  <div className="relative" style={{ transform: 'translateZ(40px)' }}>
                    <div className="absolute -inset-2 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-full blur-md opacity-80" />
                    <div className="relative w-36 h-36 rounded-full overflow-hidden border-4 border-white/20 shadow-xl">
                      <EditableImage
                        pageId="home"
                        sectionId="hero"
                        fieldId="profileImage"
                        defaultSrc={personalInfo.profileImage}
                        alt={personalInfo.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-2 shadow-lg">
                      <Sparkles size={16} className="text-black" />
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div className="text-center mb-2">
                  <EditableText
                    pageId="home"
                    sectionId="hero"
                    fieldId="name"
                    defaultValue={personalInfo.name}
                    as="h1"
                    className="text-3xl font-bold text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  />
                </div>

                {/* Gradient Divider */}
                <div className="w-24 h-1 bg-gradient-to-r from-white/70 via-white/40 to-transparent mx-auto my-4 rounded-full" />

                {/* Title */}
                <div className="text-center text-lg">
                  <EditableText
                    pageId="home"
                    sectionId="hero"
                    fieldId="title1"
                    defaultValue="Content Creator"
                    as="span"
                    className="bg-gradient-to-r from-white via-neutral-300 to-neutral-400 bg-clip-text text-transparent font-semibold"
                  />
                  <br />
                  <span className="text-neutral-500">&</span>
                  <br />
                  <EditableText
                    pageId="home"
                    sectionId="hero"
                    fieldId="title2"
                    defaultValue="Senior Business Analyst"
                    as="span"
                    className="bg-gradient-to-r from-neutral-300 via-neutral-400 to-neutral-500 bg-clip-text text-transparent font-semibold"
                  />
                </div>

                {/* Social Links */}
                <div className="flex justify-center items-center space-x-4 mt-6">
                  <a
                    href={personalInfo.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white hover:border-transparent hover:text-black text-neutral-300 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-white/20"
                    aria-label="Twitter"
                  >
                    <Twitter size={20} />
                  </a>
                  <a
                    href={personalInfo.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white hover:border-transparent hover:text-black text-neutral-300 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-white/20"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={20} />
                  </a>
                  <a
                    href={personalInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white hover:border-transparent hover:text-black text-neutral-300 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-white/20"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tagline Card */}
          <div className="flex justify-center lg:justify-start order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 50, rotateX: -20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-lg w-full space-y-6"
              style={{ transformPerspective: 1200 }}
            >
              {/* Tagline */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-white/15 via-white/10 to-white/15 rounded-2xl blur opacity-50 group-hover:opacity-80 transition-opacity duration-500" />
                <div className="relative bg-white/[0.04] backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                  <EditableText
                    pageId="home"
                    sectionId="hero"
                    fieldId="tagline"
                    defaultValue={personalInfo.tagline}
                    as="h2"
                    className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-400 bg-clip-text text-transparent leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  />
                </div>
              </div>

              {/* Bio — inverted white card for black/white contrast punch */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-white/30 via-white/20 to-white/30 rounded-2xl blur opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                <div className="relative bg-white backdrop-blur-xl rounded-2xl p-6 border border-white/40">
                  <EditableText
                    pageId="home"
                    sectionId="hero"
                    fieldId="bio"
                    defaultValue={personalInfo.bio}
                    as="p"
                    className="text-neutral-900 leading-relaxed text-sm lg:text-base"
                    style={{ textAlign: 'justify' }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden lg:block">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
